import { logger } from "@scott/logger";
import type { GeometryMeasurement } from "@/scott/ui/blocks/geometry-table/geometry-table";
import { getProductDetail } from "@/services/product/get-product-detail";

export interface GeometryParameters {
  sizes: string[];
  measurements: GeometryMeasurement[];
  imageUrl?: string;
}

export interface RawGeometryValueCell {
  mm?: string;
  inch?: string;
}

export interface RawGeometryRow {
  letter?: string;
  description?: string;
  values?: RawGeometryValueCell[];
  isAngle?: boolean;
}

export interface RawProductGeometry {
  imageUrl?: string;
  table?: {
    sizes?: string[];
    rows?: RawGeometryRow[];
  };
}

const EMPTY_GEOMETRY: GeometryParameters = {
  sizes: [],
  measurements: [],
};

export function mapRowToMeasurement(
  row: RawGeometryRow,
  sizes: string[]
): GeometryMeasurement | null {
  const label = row.description?.trim() || row.letter?.trim();

  if (!label) {
    return null;
  }

  const values: Record<string, string> = {};
  const valuesInches: Record<string, string> = {};

  const formatValue = (value: string, unit: string) => {
    if (row.isAngle) {
      return value.endsWith("°") ? value : `${value}°`;
    }
    return value.endsWith(unit) ? value : `${value} ${unit}`;
  };

  for (const [index, size] of sizes.entries()) {
    const cell = row.values?.[index];

    if (!cell || size.length === 0) {
      continue;
    }

    const mmValue = cell.mm?.trim();
    const inchValue = cell.inch?.trim();

    if (mmValue) {
      values[size] = formatValue(mmValue, "mm");
    }

    if (inchValue) {
      valuesInches[size] = formatValue(inchValue, "in");
    }
  }

  return {
    label,
    letter: row.letter?.trim() || undefined,
    values,
    valuesInches:
      Object.keys(valuesInches).length > 0 ? valuesInches : undefined,
  };
}

function transformGeometryData(
  rawGeometryData: RawProductGeometry | null | undefined
): GeometryParameters {
  if (!rawGeometryData) {
    return EMPTY_GEOMETRY;
  }

  try {
    const parsedSizes = Array.isArray(rawGeometryData.table?.sizes)
      ? rawGeometryData.table.sizes.filter(
          (size): size is string => typeof size === "string" && size.length > 0
        )
      : [];

    const measurements = Array.isArray(rawGeometryData.table?.rows)
      ? rawGeometryData.table.rows
          .map((row) => mapRowToMeasurement(row, parsedSizes))
          .filter((item): item is GeometryMeasurement => item !== null)
      : [];

    if (parsedSizes.length === 0 || measurements.length === 0) {
      return {
        ...EMPTY_GEOMETRY,
        imageUrl: rawGeometryData.imageUrl,
      };
    }

    return {
      sizes: parsedSizes,
      measurements,
      imageUrl: rawGeometryData.imageUrl,
    };
  } catch (error) {
    logger.warn("transformGeometryData: failed to parse geometry data", {
      error,
    });
    return EMPTY_GEOMETRY;
  }
}

export async function getGeometryParameters(
  productCodes: string[],
  language: string
): Promise<GeometryParameters> {
  for (const productCode of productCodes) {
    const detailData = await getProductDetail({
      productCode,
      language,
      suppressErrorLogging: true,
    });

    const productGeometry = (
      detailData as { productGeometry?: RawProductGeometry } | undefined
    )?.productGeometry;

    if (productGeometry) {
      // First product with geometry data wins — stop requesting the rest
      return transformGeometryData(productGeometry);
    }
  }

  logger.warn("getGeometryParameters: no geometry data found for products", {
    productCodes,
  });

  return transformGeometryData(null);
}
